import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Pagination,
} from '@ads/components-react';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { ExamGridListItemViewModel } from '@/shared/api/generated/types/ExamGridListItemViewModel';
import type { ExamItemLangGridItemViewModel } from '@/shared/api/generated/types/ExamItemLangGridItemViewModel';
import type { ExamItemPreviewViewModel } from '@/shared/api/generated/types/ExamItemPreviewViewModel';
import type { ExamLangListItemViewModel } from '@/shared/api/generated/types/ExamLangListItemViewModel';
import { Skeleton } from '@/shared/ui/atoms';
import { AlternativeList, QuestionCardHeader, QuestionSection } from '@/shared/ui/molecules';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Props do ExamPreviewTemplate — visualização somente-leitura da prova
 * no estilo da interface do aluno.
 */
export interface ExamPreviewTemplateProps {
  /** Dados gerais da avaliação */
  readonly exam: ExamGridListItemViewModel;
  /** Lista de questões do idioma selecionado */
  readonly questions: readonly ExamItemLangGridItemViewModel[];
  /** Índice (0-based) da questão atual */
  readonly currentIndex: number;
  /** Callback para navegar para outra questão */
  readonly onNavigate: (index: number) => void;
  /** Dados de preview da questão atual (enunciado + alternativas) */
  readonly currentPreview: ExamItemPreviewViewModel | null;
  /** Indica se o preview da questão atual está carregando */
  readonly isLoadingPreview: boolean;
  /** Idiomas disponíveis para a avaliação */
  readonly languages: readonly ExamLangListItemViewModel[];
  /** ID do idioma atualmente selecionado */
  readonly selectedLangId: number;
  /** Callback chamado ao trocar o idioma de visualização */
  readonly onLanguageChange: (langId: number) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Template de visualização de avaliação no estilo da interface do aluno.
 *
 * Exibe título da avaliação, questão atual com alternativas (quando disponíveis)
 * e navegação paginada entre questões. Tudo em modo somente-leitura.
 *
 * @example
 * ```tsx
 * <ExamPreviewTemplate
 *   exam={examData}
 *   questions={questions}
 *   currentIndex={0}
 *   onNavigate={setCurrentIndex}
 *   currentPreview={previewData}
 *   isLoadingPreview={false}
 *   languages={languages}
 *   selectedLangId={1}
 *   onLanguageChange={handleLanguageChange}
 * />
 * ```
 */
export function ExamPreviewTemplate({
  exam,
  questions,
  currentIndex,
  onNavigate,
  currentPreview,
  isLoadingPreview,
  languages,
  selectedLangId,
  onLanguageChange,
}: ExamPreviewTemplateProps) {
  const { t } = useTranslation('examManagement');

  const totalQuestions = questions.length;
  const currentQuestion = questions[currentIndex] ?? null;

  const options = currentPreview?.examTypeList ?? [];
  const questionLabel = currentPreview?.examItem?.label ?? currentQuestion?.label ?? null;
  const hasOptions = options.length > 0;

  const componentType =
    currentPreview?.examItem?.componentType ?? currentQuestion?.componentType ?? null;
  const questionTypeLabel = componentType
    ? t(`questionType.${componentType}` as never, {
        defaultValue: componentType,
      })
    : null;

  return (
    <div className="flex flex-col gap-sm">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbLink>
          <Link to="/">
            <BreadcrumbIcon />
          </Link>
        </BreadcrumbLink>
        <BreadcrumbLink>
          <Link to={'/exams' as never}>{t('breadcrumb.exams')}</Link>
        </BreadcrumbLink>
        <BreadcrumbLink>
          <Link to={'/exams/$id' as never} params={{ id: String(exam.id ?? 0) } as never}>
            {exam.title ?? ''}
          </Link>
        </BreadcrumbLink>
        <BreadcrumbPage>{t('preview.breadcrumb')}</BreadcrumbPage>
      </Breadcrumb>

      {/* Page Title */}
      <div className="flex flex-col gap-quark">
        <span className="ts-heading-lg text-ctx-content-title">{exam.title ?? ''}</span>
        <span className="ts-paragraph-xs text-ctx-content-placeholder">
          {exam.disciplineText ?? exam.examTypeText ?? '—'}
        </span>
      </div>

      {/* Question Card */}
      <div className="overflow-hidden rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level1">
        {/* Card Header */}
        <QuestionCardHeader
          label={
            totalQuestions > 0
              ? t('preview.question', { number: currentIndex + 1 })
              : t('preview.breadcrumb')
          }
          tagLabel={questionTypeLabel}
          icon={ClipboardDocumentListIcon}
        />

        {/* Language Tabs */}
        {languages.length > 0 && (
          <div className="flex items-center gap-xs border-b-hairline border-ctx-layout-border px-xs py-quark">
            {languages.map(lang => (
              <button
                key={lang.id}
                type="button"
                onClick={() => onLanguageChange(lang.id ?? 0)}
                className={[
                  'ts-paragraph-xs pb-quark transition-colors',
                  lang.id === selectedLangId
                    ? 'border-b-thin border-ctx-content-title !font-semibold text-ctx-content-title'
                    : 'text-ctx-content-base hover:text-ctx-content-title',
                ].join(' ')}
              >
                {lang.cultureInfoDisplay ?? lang.cultureInfo ?? ''}
              </button>
            ))}
          </div>
        )}

        {/* Question Body */}
        <div className="flex flex-col gap-mini p-xs">
          {totalQuestions === 0 ? (
            <div className="flex items-center justify-center py-xl">
              <span className="ts-paragraph-xs text-ctx-content-placeholder">
                {t('preview.noQuestions')}
              </span>
            </div>
          ) : (
            <>
              {/* Enunciado */}
              <QuestionSection label={t('preview.statement')}>
                {isLoadingPreview ? (
                  <div className="flex flex-col gap-quark">
                    <Skeleton className="h-mini w-full" />
                    <Skeleton className="h-mini w-full" />
                    <Skeleton className="h-mini w-3/4" />
                  </div>
                ) : (
                  <p
                    className="rich-text-content ts-paragraph-xs text-ctx-content-base [&_.code-display]:rounded-sm [&_.code-display]:bg-gray-100 [&_.code-display]:px-2 [&_.code-display]:py-1 [&_.code-display]:font-mono [&_.code-display]:text-sm [&_.code-display]:text-gray-800 [&_*]:!font-base [&_*]:!text-xs [&_*]:!leading-lg [&_*]:!text-ctx-content-base"
                    dangerouslySetInnerHTML={{ __html: questionLabel ?? '' }}
                  />
                )}
              </QuestionSection>

              {/* Alternativas */}
              <div className="flex flex-col gap-nano">
                <span className="ts-heading-xxs text-ctx-content-title">
                  {t('preview.alternatives')}
                </span>
                {isLoadingPreview ? (
                  <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border">
                    {[0, 1, 2, 3].map(i => (
                      <div key={i}>
                        <div className="flex items-center gap-micro px-mini py-micro">
                          <Skeleton shape="circle" className="h-xs w-xs shrink-0" />
                          <Skeleton className="h-mini w-1/2" />
                        </div>
                        {i < 3 && <div className="h-px bg-ctx-layout-border" />}
                      </div>
                    ))}
                  </div>
                ) : hasOptions ? (
                  <AlternativeList options={options} />
                ) : (
                  <p className="italic ts-caption-xxs text-ctx-content-placeholder">
                    {t('preview.openAnswer')}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Pagination */}
        {totalQuestions > 0 && (
          <div className="flex justify-center px-xs pb-xs">
            <Pagination
              pageAmount={totalQuestions}
              activeIndex={currentIndex}
              setActiveIndex={v => onNavigate(typeof v === 'function' ? v(currentIndex) : v)}
              ellipsisLabel="…"
            />
          </div>
        )}
      </div>
    </div>
  );
}
